import React from "react";
import { MdAccountBalanceWallet, MdCheck, MdClose, MdRefresh, MdSearch } from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { adminService } from "services/adminService";
import type {
  AdminAppleWalletTopup,
  AdminWalletListItem,
  AdminWalletSummary,
  AdminWalletTopupRequest,
} from "services/adminWalletService";
import DataTablePagination from "components/ui/DataTablePagination";
import { DataTableEmptyState, DataTableLoadingState } from "components/ui/DataTableStates";
import StudentWalletModal from "views/admin/students/components/StudentWalletModal";
import { toastError, toastSuccess } from "lib/toast";
import { formatLocaleDateTime, formatLocaleNumber, toWesternDigits } from "lib/localeFormat";

type TabId = "wallets" | "apple" | "manual";

function money(value: number | null | undefined, currency: string, isArabic: boolean): string {
  if (value === null || value === undefined) return "—";
  return formatLocaleNumber(value, isArabic, { maximumFractionDigits: 2 }) + " " + currency;
}

function formatDate(value: string | null | undefined, isArabic: boolean): string {
  return formatLocaleDateTime(value, isArabic, { dateStyle: "medium", timeStyle: "short" });
}

function paymentMethodLabel(method: string, isArabic: boolean): string {
  if (method === "vodafone_cash") return isArabic ? "فودافون كاش" : "Vodafone Cash";
  if (method === "instapay") return isArabic ? "إنستا باي" : "InstaPay";
  return method;
}

function statusLabel(status: string, isArabic: boolean): string {
  const map: Record<string, [string, string]> = {
    pending: ["قيد المراجعة", "Pending"],
    approved: ["مقبول", "Approved"],
    rejected: ["مرفوض", "Rejected"],
    cancelled: ["ملغي", "Cancelled"],
  };
  const pair = map[status];
  return pair ? (isArabic ? pair[0] : pair[1]) : status;
}

function ApproveManualDialog({
  request,
  isArabic,
  onClose,
  onDone,
}: {
  request: AdminWalletTopupRequest | null;
  isArabic: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = React.useState("");
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!request) return;
    setAmount(String(request.amount ?? ""));
    setNote("");
  }, [request]);

  if (!request) return null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await adminService.approveWalletTopupRequest(request.id, {
        amount: amount !== "" ? Number(amount) : undefined,
        admin_note: note.trim() || undefined,
      });
      toastSuccess(isArabic ? "تم قبول الطلب وإضافة الرصيد." : "Request approved and balance credited.");
      onDone();
      onClose();
    } catch {
      toastError(isArabic ? "تعذر قبول الطلب." : "Could not approve the request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="presentation">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl dark:bg-navy-800"
        dir={isArabic ? "rtl" : "ltr"}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              {isArabic ? "قبول طلب الشحن" : "Approve top-up request"}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
              {request.student?.name ?? "—"} · {paymentMethodLabel(request.payment_method, isArabic)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
            aria-label={isArabic ? "إغلاق" : "Close"}
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>

        {request.payment_proof_url ? (
          <a
            href={request.payment_proof_url}
            target="_blank"
            rel="noreferrer"
            className="mt-4 block overflow-hidden rounded-xl border border-gray-200 dark:border-white/10"
          >
            <img
              src={request.payment_proof_url}
              alt={isArabic ? "إثبات الدفع" : "Payment proof"}
              className="max-h-56 w-full object-contain bg-gray-50 dark:bg-navy-900"
            />
          </a>
        ) : null}

        <label className="mt-4 block text-sm font-semibold text-gray-700 dark:text-slate-200">
          {isArabic ? "المبلغ المُضاف" : "Credit amount"}
          <input
            required
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-transparent px-3 py-2 font-normal dark:border-white/10"
          />
        </label>
        <label className="mt-3 block text-sm font-semibold text-gray-700 dark:text-slate-200">
          {isArabic ? "ملاحظة (اختياري)" : "Note (optional)"}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={2000}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-transparent px-3 py-2 font-normal dark:border-white/10"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
          <button
            disabled={saving}
            type="submit"
            className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            <MdCheck className="h-4 w-4" />
            {saving ? "…" : isArabic ? "قبول وإضافة الرصيد" : "Approve and credit"}
          </button>
        </div>
      </form>
    </div>
  );
}

function RejectManualDialog({
  request,
  isArabic,
  onClose,
  onDone,
}: {
  request: AdminWalletTopupRequest | null;
  isArabic: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  if (!request) return null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      await adminService.rejectWalletTopupRequest(request.id, {
        admin_note: note.trim() || undefined,
      });
      toastSuccess(isArabic ? "تم رفض الطلب." : "Request rejected.");
      onDone();
      onClose();
    } catch {
      toastError(isArabic ? "تعذر رفض الطلب." : "Could not reject the request.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" role="presentation">
      <form
        onSubmit={submit}
        className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl dark:bg-navy-800"
        dir={isArabic ? "rtl" : "ltr"}
      >
        <h2 className="text-lg font-bold text-navy-700 dark:text-white">
          {isArabic ? "رفض طلب الشحن" : "Reject top-up request"}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">
          {request.student?.name ?? "—"} · {money(request.amount, request.currency, isArabic)}
        </p>
        <label className="mt-4 block text-sm font-semibold text-gray-700 dark:text-slate-200">
          {isArabic ? "سبب الرفض (اختياري)" : "Reason (optional)"}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={2000}
            className="mt-1 w-full rounded-xl border border-gray-200 bg-transparent px-3 py-2 font-normal dark:border-white/10"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 dark:bg-white/10 dark:text-white"
          >
            {isArabic ? "إلغاء" : "Cancel"}
          </button>
          <button
            disabled={saving}
            type="submit"
            className="inline-flex items-center gap-1.5 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            <MdClose className="h-4 w-4" />
            {saving ? "…" : isArabic ? "رفض الطلب" : "Reject request"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminWalletsPage() {
  const isArabic = useAppLanguage() === "ar";
  const [tab, setTab] = React.useState<TabId>("manual");
  const [summary, setSummary] = React.useState<AdminWalletSummary | null>(null);
  const [wallets, setWallets] = React.useState<AdminWalletListItem[]>([]);
  const [topups, setTopups] = React.useState<AdminAppleWalletTopup[]>([]);
  const [manualRequests, setManualRequests] = React.useState<AdminWalletTopupRequest[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [appliedSearch, setAppliedSearch] = React.useState("");
  const [hasBalanceOnly, setHasBalanceOnly] = React.useState(false);
  const [revokedFilter, setRevokedFilter] = React.useState("");
  const [manualStatus, setManualStatus] = React.useState("pending");
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [range, setRange] = React.useState({ from: 0, to: 0 });
  const [walletStudent, setWalletStudent] = React.useState<AdminWalletListItem | null>(null);
  const [approveTarget, setApproveTarget] = React.useState<AdminWalletTopupRequest | null>(null);
  const [rejectTarget, setRejectTarget] = React.useState<AdminWalletTopupRequest | null>(null);
  const [reloadTick, setReloadTick] = React.useState(0);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "wallets") {
        const [summaryResult, listResult] = await Promise.all([
          adminService.getWalletSummary(),
          adminService.getWallets({
            page,
            limit: 15,
            term: appliedSearch || undefined,
            has_balance: hasBalanceOnly ? "1" : undefined,
            sort_by: "balance",
            sort_direction: "desc",
          }),
        ]);
        setSummary(summaryResult);
        setWallets(listResult.data || []);
        setLastPage(Math.max(1, Number(listResult.meta?.last_page) || 1));
        setTotal(Number(listResult.meta?.total) || 0);
        setRange({
          from: Number(listResult.meta?.from) || 0,
          to: Number(listResult.meta?.to) || 0,
        });
      } else if (tab === "apple") {
        const [summaryResult, topupResult] = await Promise.all([
          adminService.getWalletSummary(),
          adminService.getAppleWalletTopups({
            page,
            limit: 15,
            term: appliedSearch || undefined,
            revoked: revokedFilter || undefined,
          }),
        ]);
        setSummary(summaryResult);
        setTopups(topupResult.data || []);
        setLastPage(Math.max(1, Number(topupResult.meta?.last_page) || 1));
        setTotal(Number(topupResult.meta?.total) || 0);
        setRange({
          from: Number(topupResult.meta?.from) || 0,
          to: Number(topupResult.meta?.to) || 0,
        });
      } else {
        const [summaryResult, manualResult] = await Promise.all([
          adminService.getWalletSummary(),
          adminService.getWalletTopupRequests({
            page,
            limit: 15,
            term: appliedSearch || undefined,
            status: manualStatus || undefined,
          }),
        ]);
        setSummary(summaryResult);
        setManualRequests(manualResult.data || []);
        setLastPage(Math.max(1, Number(manualResult.meta?.last_page) || 1));
        setTotal(Number(manualResult.meta?.total) || 0);
        setRange({
          from: Number(manualResult.meta?.from) || 0,
          to: Number(manualResult.meta?.to) || 0,
        });
      }
    } catch (error) {
      console.error("Failed to load wallets page:", error);
      toastError(isArabic ? "تعذر تحميل بيانات المحافظ." : "Could not load wallets data.");
    } finally {
      setLoading(false);
    }
  }, [tab, page, appliedSearch, hasBalanceOnly, revokedFilter, manualStatus, isArabic, reloadTick]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    setPage(1);
  }, [tab, appliedSearch, hasBalanceOnly, revokedFilter, manualStatus]);

  const currency = summary?.currency || "EGP";
  const summaryCards = summary
    ? [
        [isArabic ? "إجمالي الأرصدة" : "Total balance", money(summary.total_balance, currency, isArabic)],
        [isArabic ? "محافظ نشطة" : "Positive wallets", String(summary.positive_balance_count)],
        [isArabic ? "شحن Apple" : "Apple top-ups", money(summary.apple_topups_total, currency, isArabic)],
        [isArabic ? "إنفاق الكورسات" : "Course spend", money(summary.course_spend_total, currency, isArabic)],
        [isArabic ? "تعديلات الأدمن" : "Admin adjust", money(summary.admin_adjust_net, currency, isArabic)],
        [isArabic ? "شحنات ملغاة" : "Revoked top-ups", String(summary.revoked_apple_topups)],
      ]
    : [];

  const tabButton = (id: TabId, labelAr: string, labelEn: string) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      className={
        "rounded-xl px-4 py-2 text-sm font-semibold " +
        (tab === id
          ? "bg-emerald-600 text-white"
          : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white")
      }
    >
      {isArabic ? labelAr : labelEn}
    </button>
  );

  return (
    <div dir={isArabic ? "rtl" : "ltr"}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-500 dark:bg-emerald-400/10 dark:text-emerald-300">
            <MdAccountBalanceWallet className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-extrabold text-navy-700 dark:text-white">
              {isArabic ? "محافظ الطلاب" : "Student wallets"}
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
              {isArabic
                ? "متابعة الأرصدة وطلبات شحن فودافون كاش / إنستا باي وشحنات Apple."
                : "Review balances, Vodafone Cash / InstaPay top-up requests, and Apple top-ups."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setReloadTick((value) => value + 1)}
          className="inline-flex items-center gap-1.5 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
        >
          <MdRefresh className="h-4 w-4" />
          {isArabic ? "تحديث" : "Refresh"}
        </button>
      </div>

      {summaryCards.length > 0 ? (
        <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {summaryCards.map(([label, value]) => (
            <div
              key={label}
              className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800"
            >
              <p className="text-xs text-gray-500">{label}</p>
              <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">{value}</p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {tabButton("manual", "طلبات الشحن", "Top-up requests")}
        {tabButton("wallets", "المحافظ", "Wallets")}
        {tabButton("apple", "شحنات Apple", "Apple top-ups")}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <MdSearch className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 ltr:left-3 rtl:right-3" />
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") setAppliedSearch(searchTerm.trim());
            }}
            placeholder={
              tab === "apple"
                ? isArabic
                  ? "بحث بالطالب أو المنتج أو المعاملة"
                  : "Search student, product, or transaction"
                : isArabic
                  ? "بحث بالاسم أو الهاتف"
                  : "Search by name or phone"
            }
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm outline-none focus:border-emerald-500 dark:border-white/10 dark:bg-navy-900 dark:text-white ltr:pl-9 ltr:pr-3 rtl:pr-9 rtl:pl-3"
          />
        </div>
        <button
          type="button"
          onClick={() => setAppliedSearch(searchTerm.trim())}
          className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          {isArabic ? "تطبيق" : "Apply"}
        </button>
        {tab === "wallets" ? (
          <label className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-sm dark:border-white/10">
            <input
              type="checkbox"
              checked={hasBalanceOnly}
              onChange={(event) => setHasBalanceOnly(event.target.checked)}
            />
            {isArabic ? "رصيد أكبر من صفر" : "Balance > 0"}
          </label>
        ) : null}
        {tab === "apple" ? (
          <select
            value={revokedFilter}
            onChange={(event) => setRevokedFilter(event.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            <option value="">{isArabic ? "كل الشحنات" : "All top-ups"}</option>
            <option value="0">{isArabic ? "نشطة فقط" : "Active only"}</option>
            <option value="1">{isArabic ? "ملغاة فقط" : "Revoked only"}</option>
          </select>
        ) : null}
        {tab === "manual" ? (
          <select
            value={manualStatus}
            onChange={(event) => setManualStatus(event.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2.5 text-sm dark:border-white/10 dark:bg-navy-900 dark:text-white"
          >
            <option value="pending">{isArabic ? "قيد المراجعة" : "Pending"}</option>
            <option value="approved">{isArabic ? "مقبولة" : "Approved"}</option>
            <option value="rejected">{isArabic ? "مرفوضة" : "Rejected"}</option>
            <option value="cancelled">{isArabic ? "ملغاة" : "Cancelled"}</option>
            <option value="all">{isArabic ? "الكل" : "All"}</option>
          </select>
        ) : null}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        {loading ? (
          <DataTableLoadingState message={isArabic ? "جارٍ التحميل..." : "Loading..."} />
        ) : tab === "wallets" ? (
          wallets.length === 0 ? (
            <DataTableEmptyState
              title={isArabic ? "لا توجد محافظ" : "No wallets found"}
              hint={isArabic ? "جرّب تغيير البحث أو الفلاتر." : "Try changing the search or filters."}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[820px] w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-white/5">
                    <tr>
                      <th className="px-3 py-3 text-start">{isArabic ? "الطالب" : "Student"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "الهاتف" : "Phone"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "الرصيد" : "Balance"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "الحالة" : "Status"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "آخر تحديث" : "Updated"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "إجراء" : "Action"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {wallets.map((wallet) => (
                      <tr key={wallet.student_id}>
                        <td className="px-3 py-3 font-semibold text-navy-700 dark:text-white">{wallet.name}</td>
                        <td className="px-3 py-3 text-gray-600 dark:text-slate-300">
                          {toWesternDigits(wallet.phone)}
                        </td>
                        <td className="px-3 py-3 font-semibold text-emerald-600">
                          {money(wallet.balance, wallet.currency || currency, isArabic)}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={
                              "rounded-full px-2 py-1 text-[11px] font-semibold " +
                              (wallet.is_active
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
                                : "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200")
                            }
                          >
                            {wallet.is_active ? (isArabic ? "نشط" : "Active") : isArabic ? "موقوف" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-500">
                          {formatDate(wallet.wallet_updated_at, isArabic)}
                        </td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() => setWalletStudent(wallet)}
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            {isArabic ? "فتح المحفظة" : "Open wallet"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > 0 ? (
                <DataTablePagination
                  isArabic={isArabic}
                  page={page}
                  totalPages={lastPage}
                  fromItem={range.from}
                  toItem={range.to}
                  totalItems={total}
                  onPageChange={setPage}
                  className="mt-3"
                />
              ) : null}
            </>
          )
        ) : tab === "apple" ? (
          topups.length === 0 ? (
            <DataTableEmptyState
              title={isArabic ? "لا توجد شحنات Apple" : "No Apple top-ups"}
              hint={isArabic ? "جرّب تغيير البحث أو الفلتر." : "Try changing the search or filter."}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-white/5">
                    <tr>
                      <th className="px-3 py-3 text-start">{isArabic ? "التاريخ" : "Date"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "الطالب" : "Student"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "المنتج" : "Product"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "المبلغ" : "Amount"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "البيئة" : "Environment"}</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "الحالة" : "Status"}</th>
                      <th className="px-3 py-3 text-start">Txn</th>
                      <th className="px-3 py-3 text-start">{isArabic ? "إجراء" : "Action"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {topups.map((topup) => (
                      <tr key={topup.id}>
                        <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                          {formatDate(topup.purchased_at || topup.created_at, isArabic)}
                        </td>
                        <td className="px-3 py-3">
                          <p className="font-semibold text-navy-700 dark:text-white">
                            {topup.student?.name || "#" + topup.student_id}
                          </p>
                          <p className="text-xs text-gray-500">{toWesternDigits(topup.student?.phone || "")}</p>
                        </td>
                        <td className="px-3 py-3 text-xs">{topup.product_id}</td>
                        <td className="whitespace-nowrap px-3 py-3 font-semibold text-navy-700 dark:text-white">
                          {money(topup.credit_amount, currency, isArabic)}
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-500">{topup.environment || "—"}</td>
                        <td className="px-3 py-3">
                          {topup.is_revoked ? (
                            <span className="rounded-full bg-rose-100 px-2 py-1 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/10 dark:text-rose-200">
                              {(isArabic ? "ملغى" : "Revoked") +
                                (topup.revoke_reason ? " · " + topup.revoke_reason : "")}
                            </span>
                          ) : (
                            <span className="rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                              {isArabic ? "نشط" : "Active"}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 font-mono text-[11px] text-gray-500">{topup.transaction_id}</td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            onClick={() =>
                              setWalletStudent({
                                student_id: topup.student_id,
                                name: topup.student?.name || "#" + topup.student_id,
                                phone: topup.student?.phone || "",
                                is_active: true,
                                balance: 0,
                                currency,
                              })
                            }
                            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                          >
                            {isArabic ? "المحفظة" : "Wallet"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > 0 ? (
                <DataTablePagination
                  isArabic={isArabic}
                  page={page}
                  totalPages={lastPage}
                  fromItem={range.from}
                  toItem={range.to}
                  totalItems={total}
                  onPageChange={setPage}
                  className="mt-3"
                />
              ) : null}
            </>
          )
        ) : manualRequests.length === 0 ? (
          <DataTableEmptyState
            title={isArabic ? "لا توجد طلبات شحن" : "No top-up requests"}
            hint={isArabic ? "طلبات فودافون كاش وإنستا باي هتظهر هنا." : "Vodafone Cash and InstaPay requests will show here."}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[1040px] w-full text-sm">
                <thead className="bg-gray-50 dark:bg-white/5">
                  <tr>
                    <th className="px-3 py-3 text-start">{isArabic ? "التاريخ" : "Date"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "الطالب" : "Student"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "المبلغ" : "Amount"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "الطريقة" : "Method"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "المرجع" : "Reference"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "الإثبات" : "Proof"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "الحالة" : "Status"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "ملاحظة" : "Note"}</th>
                    <th className="px-3 py-3 text-start">{isArabic ? "إجراء" : "Action"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                  {manualRequests.map((item) => (
                    <tr key={item.id}>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                        {formatDate(item.created_at, isArabic)}
                      </td>
                      <td className="px-3 py-3">
                        <p className="font-semibold text-navy-700 dark:text-white">
                          {item.student?.name || "#" + item.student_id}
                        </p>
                        <p className="text-xs text-gray-500">{toWesternDigits(item.student?.phone || "")}</p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 font-semibold text-navy-700 dark:text-white">
                        {money(item.amount, item.currency || currency, isArabic)}
                      </td>
                      <td className="px-3 py-3">{paymentMethodLabel(item.payment_method, isArabic)}</td>
                      <td className="px-3 py-3 text-xs text-gray-500">{item.payment_reference || "—"}</td>
                      <td className="px-3 py-3">
                        {item.payment_proof_url ? (
                          <a
                            href={item.payment_proof_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-semibold text-emerald-600 hover:underline"
                          >
                            {isArabic ? "عرض" : "View"}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={
                            "rounded-full px-2 py-1 text-[11px] font-semibold " +
                            (item.status === "approved"
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200"
                              : item.status === "rejected"
                                ? "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200")
                          }
                        >
                          {statusLabel(item.status, isArabic)}
                        </span>
                      </td>
                      <td className="max-w-[180px] px-3 py-3 text-xs text-gray-500">
                        {item.admin_note || "—"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {item.status === "pending" ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setApproveTarget(item)}
                                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
                              >
                                {isArabic ? "قبول" : "Approve"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setRejectTarget(item)}
                                className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
                              >
                                {isArabic ? "رفض" : "Reject"}
                              </button>
                            </>
                          ) : null}
                          <button
                            type="button"
                            onClick={() =>
                              setWalletStudent({
                                student_id: item.student_id,
                                name: item.student?.name || "#" + item.student_id,
                                phone: item.student?.phone || "",
                                is_active: true,
                                balance: 0,
                                currency: item.currency || currency,
                              })
                            }
                            className="rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
                          >
                            {isArabic ? "المحفظة" : "Wallet"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > 0 ? (
              <DataTablePagination
                isArabic={isArabic}
                page={page}
                totalPages={lastPage}
                fromItem={range.from}
                toItem={range.to}
                totalItems={total}
                onPageChange={setPage}
                className="mt-3"
              />
            ) : null}
          </>
        )}
      </div>

      <StudentWalletModal
        open={walletStudent !== null}
        onClose={() => setWalletStudent(null)}
        studentId={walletStudent?.student_id ?? null}
        studentName={walletStudent?.name ?? ""}
        isArabic={isArabic}
        onUpdated={() => setReloadTick((value) => value + 1)}
      />
      <ApproveManualDialog
        request={approveTarget}
        isArabic={isArabic}
        onClose={() => setApproveTarget(null)}
        onDone={() => setReloadTick((value) => value + 1)}
      />
      <RejectManualDialog
        request={rejectTarget}
        isArabic={isArabic}
        onClose={() => setRejectTarget(null)}
        onDone={() => setReloadTick((value) => value + 1)}
      />
    </div>
  );
}
